import { useState } from "react"

import PageHeader from "../components/PageHeader"
import TextInput from "../components/TextInput"
import Button from "../components/Button"
import Spinner from "../components/Spinner"
import ModelSelect from "../components/ModelSelect"
import SliderInput from "../components/Slider"
import ErrorResult from "../components/ErrorResult"
import TextResult from "../components/TextResult"

import {
  summarizationConfig,
  SUMMARIZATION_MODELS
} from "../config/summarization"

import { type SummarizationRequest, type SummarizationResponse } from "../types/summarization"

import { callAPI } from "../lib/api"
import type { APIError } from "../types/api"

export default function Summarize() {
  const [config, setConfig] = useState<SummarizationRequest>({
    task: "summarize",
    text: "",
    model: "mbart",
    num_beams: 3,
    temperature: 0.5,
    top_k: 50
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [result, setResult] = useState<SummarizationResponse | APIError | null>(null)

  const handleRun = async () => {
    if (loading) return

    if (!config.text.trim()) {
      setResult({
        error: "Please enter some text."
      })
      return
    }

    if (
      config.text.trim().split(/\s+/).length <
      summarizationConfig.input_text_min_length
    ) {
      setResult({
        error: `Please enter at least ${summarizationConfig.input_text_min_length} words`
      })
      return
    }

    setLoading(true)
    setResult(null)

    try {
      const data = await callAPI<SummarizationRequest, SummarizationResponse>(
        summarizationConfig.endpoint,
        config
      )

      setResult(data)
    } catch (err: any) {
      setResult({
        error: err.message
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <PageHeader
        title={summarizationConfig.title}
      />

      <TextInput
        label="Input Text"
        value={config.text}
        onChange={(value) =>
          setConfig((prev) => ({ ...prev, text: value }))
        }
      />

      <ModelSelect
        models={SUMMARIZATION_MODELS}
        value={config.model}
        onChange={(value) =>
          setConfig((prev) => ({ ...prev, model: value }))
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <SliderInput
          label="Beam Width"
          value={config.num_beams}
          onChange={(value) =>
            setConfig((prev) => ({ ...prev, num_beams: value }))
          }
          min={1}
          max={7}
          step={1}
        />

        <SliderInput
          label="Temperature"
          value={config.temperature}
          onChange={(value) =>
            setConfig((prev) => ({ ...prev, temperature: value }))
          }
          min={0}
          max={1}
          step={0.1}
        />

        <SliderInput
          label="Top K"
          value={config.top_k}
          onChange={(value) =>
            setConfig((prev) => ({ ...prev, top_k: value }))
          }
          min={1}
          max={100}
          step={1}
        />
      </div>

      <Button
        label="Predict"
        onClick={handleRun}
        loading={loading}
      />

      {loading && <Spinner />}

      <div className="mt-6">
        {result === null ? null : "error" in result ? (
          <ErrorResult message={result.error} />
        ) : (
          <TextResult result={result} />
        )}
      </div>

      <img
        src="./bg.jpg"
        className="object-cover fixed top-0 left-0 w-screen h-screen -z-10 sm:object-fill"
        alt="background"
      />
    </div>
  )
}